import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Checkbox, Collapse, FormControlLabel, ListItemButton, Stack, Typography } from '@mui/material';
import {
  ListItemText,
  StyledExpand,
  StyledListItem,
  Wrapper,
  WrapperCollapseItem,
} from '@styles/Admin-tools/Workflows/WorkflowsStyle';
import { IStepListProps, IWorkflow, IWorkflowStep } from '../types';

export const StepList = ({ workflows, handleChangeDestStep, stepData }: IStepListProps) => {
  const ref = useRef<IWorkflowStep | null>(null);

  const [isExpanded, setExpanded] = useState(stepData?.reRouteDetails?.workflowId ?? '');
  const [currentStep, setCurrentStep] = useState<IWorkflowStep | null>(null);

  const handleChangeCheckbox = useCallback(
    (step: IWorkflowStep | null) => {
      setCurrentStep(step);
      handleChangeDestStep(step);
    },
    [handleChangeDestStep]
  );

  useEffect(() => {
    if (stepData && stepData.reRouteWorkflowStepId && stepData.reRouteDetails) {
      handleChangeCheckbox(ref?.current);
    }
  }, [stepData, handleChangeCheckbox]);

  const handleListItemButtonClick = useCallback(
    (workflowId: string): void => {
      const id = isExpanded !== workflowId ? workflowId : '';
      setExpanded(id);
    },
    [isExpanded]
  );

  const renderSteps = useCallback((parentName: string, steps: IWorkflowStep[]): ReactNode[] => {
    return steps.map((step: IWorkflowStep) => {
      const name = parentName ? parentName + ' - ' + step.name : step.name;
      const displayName = name.substring(name.indexOf('-') + 1);
      if (stepData?.reRouteWorkflowStepId === step.id) {
        ref.current = { ...step, stepPath: name };
      }
      const returnArray: ReactNode[] = [
        <FormControlLabel
          key={name}
          label={<Typography whiteSpace="nowrap">{displayName}</Typography>}
          control={
            <Checkbox
              checked={currentStep?.id === step.id}
              onChange={(event, checked) => {
                handleChangeCheckbox(checked ? { ...step, stepPath: name } : null);
              }}
            />
          }
        />,
      ];
      if (step.childSteps.length > 0) {
        return returnArray.concat(renderSteps(name, step.childSteps));
      }
      return returnArray;
    });
  }, [stepData?.reRouteWorkflowStepId, currentStep]);

  const renderWorkflow = useCallback(
    (wf: IWorkflow) => {
      return (
        <StyledListItem>
          <ListItemButton onClick={() => handleListItemButtonClick(wf.workflowId)}>
            <StyledExpand isOpen={wf.workflowId === isExpanded} />

            <ListItemText>{wf.workflowName}</ListItemText>
          </ListItemButton>
          <Collapse
            unmountOnExit
            in={isExpanded === wf.workflowId}
            timeout="auto"
          >
            <WrapperCollapseItem>
              <Stack>{renderSteps(wf.workflowName, wf.workflowSteps)}</Stack>
            </WrapperCollapseItem>
          </Collapse>
        </StyledListItem>
      );
    },
    [handleListItemButtonClick, renderSteps, isExpanded]
  );

  return <Wrapper padding="10px 0">{workflows.map(renderWorkflow)}</Wrapper>;
};
